<template>
  <div am-db-wrapper>
    <the-error-pane v-if="showErros" :errors="errors[$route.params.subtable]" />
    <div
      v-if="tableData"
      :style="makeStyle"
      :am-rows-mode="rowsMode($route.params.subtable)"
      am-table-wrapper
      ref="tableWrapper"
    >
      <image-modal v-if="openImageModal.open" />
      <edit-modal v-if="openModalRow !== null"/>
      <the-table-pane :style="{ 'z-index': tableData.totalRows + 2 }" />
      <div am-table-head :style="{ 'z-index': tableData.totalRows + 1 }">
        <table-head-cell
          v-for="column in tableData.columns"
          :key="column.colIndex"
          :index="column.colIndex"
          :am-col="cellStatuses('colsStates', column.colIndex)"
        >
          <div v-if="column.type === 'index'" :title="column.name">
            {{ column.name }}
          </div>
          <collumn-options
            v-else
            :column="column"
          />
        </table-head-cell>
      </div>
      <div
        :am-table="showErros ? 'error' : ''"
        v-if="tableData.parts"
        ref="table"
        v-click-outside="() => editing = null"
      >
        <template v-if="filteredParts">
          <table-cell
            v-for="(part, i) in filteredParts"
            :key="i"
            :am-col="cellStatuses('colsStates', part.colIndex)"
            :am-row="cellStatuses('rowsStates', part.rowIndex)"
            :am-updating="part.status === 'UPDATING' ? '' : null"
            :am-editing="part.index === editing ? '' : null"
            @mouseenter="setCursorPosition({ col: part.colIndex, row: part.rowIndex })"
            :class="errorPart.index === part.index ? '_border _border-color:danger': ''"
            @click="clearError(part)"
          >
            <component :is="componentName(part.category)" :data="part" @editing="editing = $event" />
          </table-cell>
        </template>
        <template v-else>
          <table-cell
            v-for="(part, i) in tableData.parts"
            :key="i"
            :am-col="cellStatuses('colsStates', part.colIndex)"
            :am-row="cellStatuses('rowsStates', part.rowIndex)"
            :am-updating="part.status === 'UPDATING' ? '' : null"
            :am-editing="part.index === editing ? '' : null"
            @mouseenter="setCursorPosition({ col: part.colIndex, row: part.rowIndex })"
            :class="errorPart.index === part.index ? '_border _border-color:danger': ''"
            @click="clearError(part)"
          >
            <component :is="componentName(part.category)" :data="part" @editing="editing = $event" />
          </table-cell>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'

import TheTablePane from './TheTablePane'
import TableHeadCell from './TableHeadCell'
import TableCell from './TableCell'
import AttrItem from './tableItems/AttrItem'
import CheckboxItem from './tableItems/CheckboxItem'
import FieldItem from './tableItems/FieldItem'
import FieldsArrayItem from './tableItems/FieldsArrayItem'
import ImageItem from './tableItems/ImageItem'
import ParamsItem from './tableItems/ParamsItem'
import SelectItem from './tableItems/SelectItem'
import StaticItem from './tableItems/StaticItem'
import AllowedparamsItem from './tableItems/AllowedparamsItem'
import EditModal from './editModal'
import ImageModal from './tableItems/imageModal'
import ParamssArrayItem from './tableItems/ParamssArrayItem'
import TheErrorPane from './TheErrorPane'
import collumnOptions from './collumnOptions'

export default {
  name: 'TheTable',
  components: {
    TheTablePane,
    TableHeadCell,
    TableCell,
    AttrItem,
    CheckboxItem,
    FieldItem,
    FieldsArrayItem,
    ImageItem,
    ParamsItem,
    SelectItem,
    StaticItem,
    AllowedparamsItem,
    EditModal,
    ImageModal,
    TheErrorPane,
    ParamssArrayItem,
    collumnOptions
  },
  props: {
    tableData: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    editing: null,
    isOpenCollumnOptions: false
  }),
  computed: {
    ...mapGetters('ui', ['tableStyle', 'rowsMode', 'getStatuses', 'isTablesStates']),
    ...mapState('parts', ['openModalRow', 'openImageModal', 'data']),
    ...mapState('partsErrors', ['showErros', 'errors']),
    ...mapState('ui', ['tables', 'errorPart']),
    ...mapState('filter', ['filteredParts']),
    defaultStyle () { return { display: 'none' } },
    makeStyle () {
      const key = this.$route.params.subtable
      if (this.filteredParts) {
        const rows = []
        this.filteredParts.forEach(el => {
          if (!rows.includes(el.rowIndex)) rows.push(el.rowIndex)
        })
        if (this.tables[key]) {
          return {
            '--cols': this.tables[key].cols.join('px ') + 'px',
            '--rows': `repeat(${rows.length}, ${this.tables[key].rows}px)`
          }
        }
      }
      return this.tableStyle(key) || { display: 'none' }
    }
  },
  methods: {
    clearError (part) {
      this.setErrorPart({ rowIndex: null, colIndex: null, index: null })
    },
    ...mapMutations('ui', ['setCursorPosition', 'setErrorPart', 'addTableStatesProperties']),
    ...mapActions('ui', ['putSettings', 'addTableProperties']),
    componentName: c => `${c[0].toUpperCase()}${c.substring(1)}Item`,
    cellStatuses (key, index) {
      return this.getStatuses({ db: this.$route.params.subtable, key: key, index: index })
    }
  },
  async created () {
    if (!this.isTablesStates(this.$route.params.subtable)) {
      this.addTableStatesProperties({
        subtable: this.$route.params.subtable
      })
    }
    this.addTableProperties({
      subtable: this.$route.params.subtable,
      ...this.tableData
    })
  }
}
</script>

<style lang="scss">
  [am-db-wrapper] {
    overflow: hidden;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    flex-grow: 1;
  }

  .-dark {
    [am-table-wrapper] { background-color: var(--color--dark); }
  }
  .-light {
    [am-table-wrapper] { background-color: var(--color--white); }
  }

  [am-table-wrapper] {
    ----font-size: 0.85rem;
    ----line-height: 1.2;
    ----spacing: 0.25rem;
    ----padding: #{var(----spacing)};

    font-size: var(----font-size);

    flex-grow: 10;
    overflow: auto;

    background-color: var(----background);

    [am-updating] {
      opacity: 0.5;
      pointer-events: none;
    }

    [am-table-pane] {
      height: 36px;
    }

    [am-table-head], [am-table] {
      display: grid;
      width: fit-content;
      height: auto;
      box-sizing: border-box;
      grid-template-columns: var(--cols);
      border-left: var(--border-left-width) var(--border-style) var(--border-color);
      justify-items: stretch;
      align-items: stretch;
    }

    [am-table-head] {
      border-top: var(--border-top-width) var(--border-style) var(--border-color);
      grid-template-rows: auto;
      position: sticky;
      top: 36px;
      z-index: 3;
    }
    [am-table] {
      p {
        line-height: 1.2;
      }
      border-bottom: var(--border-bottom-width) var(--border-style) var(--border-color);
      grid-template-rows: var(--rows);

      & [am-mode~="edit"] { line-height: var(----line-height); }
    }
    [am-table~="error"] {
      margin-right: 20rem;
    }

    &[am-rows-mode~="short"] [am-table] { line-height: calc(36px - 0.5rem); }
    &[am-rows-mode~="medium"] [am-table] { line-height: calc(27px - 0.5rem); }
    &[am-rows-mode~="tall"] [am-table] { line-height: calc(26px - 0.5rem); }
    &[am-rows-mode~="extraTall"] [am-table] { line-height: calc(28px - 0.5rem); }
  }
</style>
